import { normalizeSiret } from './api';

/** Public French company directory (Annuaire des Entreprises), no API key, CORS enabled. */
const ANNUAIRE_ENTREPRISES_SEARCH_URL = 'https://recherche-entreprises.api.gouv.fr/search';

interface AnnuaireEtablissement {
    siret?: string;
    /** Full one-line address, e.g. "111 ROUTE DE GADAGNE 84510 CAUMONT-SUR-DURANCE". */
    adresse?: string | null;
    numero_voie?: string | null;
    indice_repetition?: string | null;
    type_voie?: string | null;
    libelle_voie?: string | null;
    complement_adresse?: string | null;
    code_postal?: string | null;
    libelle_commune?: string | null;
    etat_administratif?: string | null;
}

interface AnnuaireResult {
    nom_raison_sociale?: string | null;
    nom_complet?: string | null;
    siege?: AnnuaireEtablissement;
    matching_etablissements?: AnnuaireEtablissement[];
}

export interface AnnuaireCompany {
    name: string;
    street: string;
    additionalAddress: string;
    postalCode: string;
    city: string;
    country: string;
    /** The establishment is closed ("F" in the directory). */
    closed: boolean;
}

const joinParts = (...parts: Array<string | null | undefined>) =>
    parts
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(' ');

/** Street line: from the detailed parts (only on `siege`), else the one-line address minus postcode and city. */
const streetOf = (etablissement: AnnuaireEtablissement): string => {
    const fromParts = joinParts(
        etablissement.numero_voie,
        etablissement.indice_repetition,
        etablissement.type_voie,
        etablissement.libelle_voie,
    );
    if (fromParts) {
        return fromParts;
    }
    const tail = joinParts(etablissement.code_postal, etablissement.libelle_commune);
    const adresse = (etablissement.adresse || '').trim();
    return tail && adresse.endsWith(tail) ? adresse.slice(0, -tail.length).trim() : '';
};

export const mapAnnuaireResult = (result: AnnuaireResult, siret: string): AnnuaireCompany | null => {
    // `matching_etablissements` entries only carry the one-line address, `siege` has the detailed parts.
    const etablissement =
        (result.siege?.siret === siret ? result.siege : undefined) ||
        (result.matching_etablissements || []).find((item) => item.siret === siret);
    if (!etablissement) {
        return null;
    }

    return {
        name: (result.nom_raison_sociale || result.nom_complet || '').trim(),
        street: streetOf(etablissement),
        additionalAddress: (etablissement.complement_adresse || '').trim(),
        postalCode: (etablissement.code_postal || '').trim(),
        city: (etablissement.libelle_commune || '').trim(),
        country: 'France',
        closed: etablissement.etat_administratif === 'F',
    };
};

/** Looks up an establishment by SIRET; resolves to null when it is unknown or the directory is unreachable. */
export async function fetchCompanyFromAnnuaire(siret: string): Promise<AnnuaireCompany | null> {
    const normalized = normalizeSiret(siret);
    try {
        const query = new URLSearchParams({ q: normalized, page: '1', per_page: '5' });
        const response = await fetch(`${ANNUAIRE_ENTREPRISES_SEARCH_URL}?${query.toString()}`, {
            headers: { accept: 'application/json' },
        });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        for (const result of (data?.results || []) as AnnuaireResult[]) {
            const company = mapAnnuaireResult(result, normalized);
            if (company) {
                return company;
            }
        }
        return null;
    } catch {
        return null;
    }
}
